/**
 * Public, fictional example Living Announcements used in the SEO gallery.
 * NEVER pulls from real user content. All copy is original and emotionally human.
 */

export type CategorySlug =
  | 'birthday'
  | 'gratitude'
  | 'apology'
  | 'anniversary'
  | 'love'
  | 'family';

export interface Example {
  /** Short emotional headline used as a card title */
  headline: string;
  /** "From X to Y" — fictional first names only */
  attribution: string;
  /** Full readable body for AI/SEO indexing */
  body: string;
  /** A single quotable line pulled for visual emphasis */
  pullQuote: string;
}

export interface CategoryContent {
  slug: CategorySlug;
  /** Used in <title> */
  metaTitle: string;
  metaDescription: string;
  eyebrow: string;
  h1: string;
  /** Emotional opening paragraph, ~ 2 sentences */
  lede: string;
  /** Long-form readable intro — 2-3 paragraphs for AI indexing */
  intro: string[];
  /** When to send this kind of announcement */
  whenToSend: string[];
  /** How to write one — short emotional guidance */
  howToWrite: string[];
  examples: Example[];
  /** Closing emotional reinforcement */
  closingThought: string;
  /** Accent color token (semantic, themed). One of: primary, accent, secondary */
  accent: 'primary' | 'accent' | 'secondary';
  /** Visual atmosphere descriptor (used in CSS gradient tokens) */
  atmosphere: string;
}

export const CATEGORIES: Record<CategorySlug, CategoryContent> = {
  birthday: {
    slug: 'birthday',
    metaTitle: 'Living Birthday Announcements — Real Words, Sent While It Matters | Say It NOW',
    metaDescription:
      'Birthday messages that say more than "happy birthday." Example Living Announcements that honor someone’s life while they’re still here to read them.',
    eyebrow: 'Living Announcements · Birthday',
    h1: 'Birthdays are a chance to say what they’ve never heard.',
    lede:
      'Most birthday messages disappear in a scroll. A Living Announcement gives them something to keep — your real voice, the year they finally heard it.',
    intro: [
      'A birthday is not just a date. It is proof that someone we love made it through another year of being human — through quiet wins, private grief, and the small acts of love that nobody noticed but us.',
      'A Living Birthday Announcement is a way to say what we usually wait too long to say. It is not a card. It is a moment — written down, witnessed, and sent while they are still here to feel it.',
      'Below are example birthday announcements written for real human moments: a parent turning 70, a best friend turning 30, a sibling who has been through a hard year. Use them as inspiration to write your own.',
    ],
    whenToSend: [
      'A milestone birthday — 30, 40, 50, 60, 70, 80.',
      'A birthday after a hard year — illness, loss, a difficult chapter.',
      'A birthday where you cannot be there in person.',
      'A birthday for someone who never thinks they deserve a fuss.',
    ],
    howToWrite: [
      'Name one specific year of their life you are grateful for — not a list.',
      'Tell them what you saw in them this year that they may not have seen in themselves.',
      'Avoid the word “just.” It softens love into politeness.',
      'End with a sentence they can re-read on hard days.',
    ],
    examples: [
      {
        headline: 'For my mother, on her 70th',
        attribution: 'From Elena · for her mother Maria',
        pullQuote:
          'You raised three children while becoming the woman you wanted to be. We watched. We were paying attention.',
        body:
          'Mum — seventy years. Seventy. I keep saying the number to make it feel real. You raised three children while becoming the woman you wanted to be. We watched. We were paying attention. The way you stood up for yourself in your forties taught me more about courage than any book ever did. Thank you for showing me that a life can begin again at any age. I love you. Happy birthday.',
      },
      {
        headline: 'For my best friend, on her 30th',
        attribution: 'From Priya · for her best friend Sara',
        pullQuote:
          'You are the friend I would call from the side of the road. That is not a small thing. That is everything.',
        body:
          'Sara — thirty looks soft on you. I want you to know something on this birthday. You are the friend I would call from the side of the road. You are the friend I would call before my own family. That is not a small thing. That is everything. Whatever this decade brings, it gets to bring it to a woman who knows how to love people loudly. Happy birthday, my person.',
      },
      {
        headline: 'For my brother, after a hard year',
        attribution: 'From Marcus · for his brother Daniel',
        pullQuote:
          'You did not fall apart. Even on the days you thought you did, you didn’t. I was watching.',
        body:
          'Dan — I know this past year nearly broke you. I want to say this on your birthday so you carry it into the next one. You did not fall apart. Even on the days you thought you did, you didn’t. You showed up for your kids. You answered the phone when I called at strange hours. You are the kind of man our father hoped we would become. Happy birthday, brother. I’m proud of you.',
      },
    ],
    closingThought:
      'Some birthdays only come once. Say the thing before the candle goes out.',
    accent: 'primary',
    atmosphere: 'warm-amber',
  },

  gratitude: {
    slug: 'gratitude',
    metaTitle: 'Living Gratitude Announcements — Thank Them While They Can Read It | Say It NOW',
    metaDescription:
      'Gratitude messages that go beyond "thank you." Example Living Announcements for the people who shaped your life — sent while they’re still here.',
    eyebrow: 'Living Announcements · Gratitude',
    h1: 'Some people never know what they did for you. Tell them.',
    lede:
      'The teacher. The friend who answered at 2am. The boss who believed you before you did. A Living Gratitude Announcement is for them — in words, on the record.',
    intro: [
      'Gratitude is a strange thing. We feel it constantly and say it rarely. The people who carried us through the worst chapters often have no idea they did. They moved on. They forgot the sentence that changed our lives.',
      'A Living Gratitude Announcement repairs that silence. It tells them — clearly, in writing — exactly what they did and what it meant. Not a generic thank you. The specific one only you can write.',
      'These example announcements show how to write gratitude that lands: specific, concrete, and addressed to a real person on a real day.',
    ],
    whenToSend: [
      'You finally have the words for something they did years ago.',
      'They are retiring, moving away, or stepping back.',
      'You realize you never properly thanked them.',
      'You want them to know before you cannot tell them.',
    ],
    howToWrite: [
      'Name the exact moment — date, room, sentence — if you can.',
      'Tell them what your life looks like now because of it.',
      'Do not apologize for taking so long to say it.',
      'End with one sentence that names what they are to you.',
    ],
    examples: [
      {
        headline: 'For the teacher who saw me',
        attribution: 'From Jordan · for Mr. Alvarez, ninth-grade English',
        pullQuote:
          'You wrote one sentence in the margin of an essay: "You are a writer." I have not stopped being one since.',
        body:
          'Mr. Alvarez — you probably do not remember me. I was the quiet one in the third row in 2009. You wrote one sentence in the margin of an essay: "You are a writer." I have not stopped being one since. I am thirty now. I write for a living. I wanted you to know that a single sentence from a teacher can outlast a marriage, a city, a decade. Thank you. Truly. From a student who has finally grown into the sentence you wrote.',
      },
      {
        headline: 'For the friend who stayed',
        attribution: 'From Amara · for her friend Lila',
        pullQuote:
          'You sat on my kitchen floor with me at 3am. You did not try to fix it. You just stayed.',
        body:
          'Lila — I want to say this in writing because in person I get clumsy. You sat on my kitchen floor with me at 3am the night everything fell apart. You did not try to fix it. You just stayed. I am still alive because of how you stayed. I do not know how to repay something like that, so I will just say it out loud, on the record, where you can re-read it: thank you. You are family.',
      },
      {
        headline: 'For a mentor at work',
        attribution: 'From Theo · for his manager Janet',
        pullQuote:
          'You promoted me before I felt ready. That decision rewrote what I believed I was capable of.',
        body:
          'Janet — you are retiring next month and I refuse to let you go without saying this. You promoted me before I felt ready. That decision rewrote what I believed I was capable of. Everything good in my career traces back to a meeting on a Tuesday in 2019 where you said, "I think you can do this." Thank you for spending your authority on me. I will spend mine on someone else, in your name.',
      },
    ],
    closingThought:
      'Gratitude that stays unsaid is a gift that never reaches its owner. Send it.',
    accent: 'accent',
    atmosphere: 'soft-gold',
  },

  apology: {
    slug: 'apology',
    metaTitle: 'Living Apology Announcements — Real Apologies, Written Without Excuses | Say It NOW',
    metaDescription:
      'Apology messages that take responsibility without explaining it away. Example Living Announcements for the apology you’ve been carrying.',
    eyebrow: 'Living Announcements · Apology',
    h1: 'The apology you’ve been carrying belongs to them, not you.',
    lede:
      'A real apology does not ask to be accepted. It just tells the truth. A Living Apology Announcement gives that truth a place to land.',
    intro: [
      'Most apologies fail because they are about the apologizer — their guilt, their need to be forgiven, their wish to feel lighter. A real apology is about the person who was hurt.',
      'A Living Apology Announcement is written for them. It names what happened. It does not explain it away. It does not ask for anything. It releases the truth so they can do whatever they need to do with it.',
      'These example apology announcements show how to write something honest — to a parent, a friend, a former partner, a child — without hiding behind "I’m sorry if." ',
    ],
    whenToSend: [
      'You finally understand what you did, even years later.',
      'You owe someone the truth before they hear a version of it from someone else.',
      'You want them to know you remember — and that you are not pretending.',
      'You want to apologize without asking for anything back.',
    ],
    howToWrite: [
      'Name what you did. Use the word "I." Avoid the word "if."',
      'Do not explain why. Explanations sound like defense.',
      'Tell them what you understand now that you did not understand then.',
      'Make no request. Forgiveness is not yours to ask for.',
    ],
    examples: [
      {
        headline: 'To my mother, twenty years late',
        attribution: 'From David · for his mother Helen',
        pullQuote:
          'I blamed you for things that were not yours. I knew it then and I let you carry it anyway.',
        body:
          'Mum — I have owed you this for twenty years. I blamed you for things that were not yours. I knew it then and I let you carry it anyway, because it was easier than blaming myself. You did not deserve the way I spoke to you in my twenties. You did not deserve my silence in my thirties. I see you now. I see what you carried. I am sorry. I am not asking for anything. I just wanted you to hear it from me, in writing, where you can keep it.',
      },
      {
        headline: 'To a friend I let drift',
        attribution: 'From Naomi · for her friend Rachel',
        pullQuote:
          'I stopped calling because I was ashamed of my life, and I let you think it was about you.',
        body:
          'Rach — I owe you a real apology, not the awkward one I tried to give over coffee. I stopped calling because I was ashamed of my life, and I let you think it was about you. That was not fair. You reached out three times. I remember each one. I was the friend who disappeared, not the friend who got busy. I am sorry. You were good to me. You deserved better than my silence.',
      },
      {
        headline: 'To my child, now grown',
        attribution: 'From Marco · for his son Luca',
        pullQuote:
          'I was angry at the world and I aimed it at the smallest person in the room. I am so sorry.',
        body:
          'Luca — you are twenty-five now and a better man than I was at your age. I want to say something I should have said a long time ago. When you were small, I was angry at the world and I aimed it at the smallest person in the room. I am so sorry. You were a child. You did nothing to deserve the way I spoke to you on hard days. I have spent years trying to be a different father. I will spend the rest of my life trying. I love you.',
      },
    ],
    closingThought:
      'A true apology asks for nothing. It only tells the truth — and lets the other person do what they need to do with it.',
    accent: 'secondary',
    atmosphere: 'dusk-rose',
  },

  anniversary: {
    slug: 'anniversary',
    metaTitle: 'Living Anniversary Announcements — Honor the Years Out Loud | Say It NOW',
    metaDescription:
      'Anniversary messages for partners, parents, and friendships that lasted. Example Living Announcements that honor years of love — in real words.',
    eyebrow: 'Living Announcements · Anniversary',
    h1: 'The years are the gift. Say what they’ve meant.',
    lede:
      'An anniversary is not just a number. It is proof of choosing each other — through quiet mornings, hard nights, and everything in between. A Living Anniversary Announcement is how you mark that out loud.',
    intro: [
      'Anniversaries are often celebrated with dinners and gifts and photos. But the most lasting thing you can give the person who stayed is words — clear, specific, true ones.',
      'A Living Anniversary Announcement is a way to put on the record what your years together have meant. Not a toast. A document. Something they can re-read on the days when love feels far away.',
      'These examples show how to write anniversary messages for partners, for parents marking their fiftieth, and for friendships that have outlasted everything.',
    ],
    whenToSend: [
      'A milestone year — 1, 5, 10, 25, 50.',
      'An anniversary that follows a hard chapter you survived together.',
      'A parent or grandparent anniversary you want to honor publicly.',
      'A friendship anniversary — the friend who has been there a decade or more.',
    ],
    howToWrite: [
      'Name one specific year of your life together. Why it mattered.',
      'Tell them what you know now that you did not know on day one.',
      'Skip the cliché. They have heard "you complete me." They have not heard your truth.',
      'End with a sentence that names what you choose, today, again.',
    ],
    examples: [
      {
        headline: 'Ten years, in real words',
        attribution: 'From Sam · for his partner Iris',
        pullQuote:
          'I did not know on day one that love is mostly small decisions made at 6am in a quiet kitchen.',
        body:
          'Iris — ten years. I did not know on day one that love is mostly small decisions made at 6am in a quiet kitchen. I did not know it would look like you making coffee for me even when you were angry with me. I know now. Thank you for ten years of small, unglamorous, life-saving choices. I choose you again today. I will keep choosing you.',
      },
      {
        headline: 'For my parents, on their 50th',
        attribution: 'From Hannah · for Walter and Joan',
        pullQuote:
          'You taught us what staying looks like when staying is the hardest thing in the room.',
        body:
          'Mum and Dad — fifty years. We grew up watching you choose each other, even on the years it was clearly hard. You taught us what staying looks like when staying is the hardest thing in the room. That lesson is in every relationship we have. We are who we are because of who you are to each other. Thank you for fifty years of being a family we wanted to belong to.',
      },
      {
        headline: 'Twenty years of friendship',
        attribution: 'From Mei · for her friend Olivia',
        pullQuote:
          'You have known me through four cities, three jobs, two heartbreaks, and one self I am proud of.',
        body:
          'Liv — twenty years of friendship today. You have known me through four cities, three jobs, two heartbreaks, and one self I am proud of. There are people in my life I had to become someone else for. You are not one of them. Thank you for two decades of letting me be exactly who I am, even on the years I did not love who I was. Here is to twenty more.',
      },
    ],
    closingThought:
      'The years deserve more than a toast. They deserve a sentence they can keep.',
    accent: 'primary',
    atmosphere: 'champagne-glow',
  },

  love: {
    slug: 'love',
    metaTitle: 'Living Love Announcements — Say It Plainly, Say It Now | Say It NOW',
    metaDescription:
      'Love messages without the cliché. Example Living Announcements for partners, soulmates, and the people you’ve never quite told.',
    eyebrow: 'Living Announcements · Love',
    h1: 'Love is what you say when you stop being clever.',
    lede:
      'The bravest love letters are the plain ones. A Living Love Announcement is for the people you have never quite found the right words for — until you decided to use the real ones.',
    intro: [
      'Most love messages hide behind cleverness. The good ones do the opposite. They get out of the way and let the truth through.',
      'A Living Love Announcement is for the person you love — partner, soulmate, the one you have been quietly loving for years. It says what you actually feel, in a sentence they could put on a wall.',
      'These examples show how to write love without cliché, without performance, and without the safety net of a joke.',
    ],
    whenToSend: [
      'You realize you have never said it the way you mean it.',
      'A quiet Tuesday — when nothing is on fire and you just love them.',
      'A turning point — a move, a child, a hard diagnosis.',
      'A first anniversary, or a fortieth.',
    ],
    howToWrite: [
      'Cut every adjective you can. Keep the verbs.',
      'Use their name. Not "babe." Not "you." Their name.',
      'Tell them one specific thing you love that no one else has noticed.',
      'End with a sentence that sounds like you, not like a card.',
    ],
    examples: [
      {
        headline: 'On a Tuesday, for no reason',
        attribution: 'From Owen · for his partner Maya',
        pullQuote:
          'I love the way you laugh at your own jokes a half-second before the punchline. It is the most generous thing about you.',
        body:
          'Maya — it is Tuesday. Nothing is happening. I just wanted to say it out loud somewhere I cannot take back. I love the way you laugh at your own jokes a half-second before the punchline. It is the most generous thing about you. I love the way you set the table even when we are tired. I love that you are mine and that you chose me on purpose. I love you. That is the whole announcement.',
      },
      {
        headline: 'The one I never told',
        attribution: 'From Aria · for her partner Noah',
        pullQuote:
          'I have loved you for years and never told you in a sentence you could keep. Keep this one.',
        body:
          'Noah — I have loved you for years and never told you in a sentence you could keep. So keep this one. I love you the way you make tea: slowly, with attention, like it matters. I love your quiet voice on the phone. I love that you came back, even when it would have been easier not to. You are the one. You have been the one for a long time. I just finally said it where it counts.',
      },
      {
        headline: 'For the long love',
        attribution: 'From Stephen · for his husband Tom',
        pullQuote:
          'After thirty years, I still look up when you walk into the room. That has not changed once.',
        body:
          'Tom — thirty years of you. I want to put this somewhere it cannot get lost. After thirty years, I still look up when you walk into the room. That has not changed once. Not in the hard year. Not in the boring year. Not in the year we almost lost it. You are my favorite person. You will always be my favorite person. I love you. Plainly. Fully. On the record.',
      },
    ],
    closingThought:
      'Love that stays in your head is just thinking. Send it.',
    accent: 'secondary',
    atmosphere: 'rose-amber',
  },

  family: {
    slug: 'family',
    metaTitle: 'Living Family Announcements — Words for Parents, Siblings, Children | Say It NOW',
    metaDescription:
      'Family messages with no small talk. Example Living Announcements for parents, siblings, and children — sent while everyone is still here to read them.',
    eyebrow: 'Living Announcements · Family',
    h1: 'Family is the conversation we keep meaning to have.',
    lede:
      'The hardest words to say are usually to the people we have known the longest. A Living Family Announcement gives those words a place — and a moment — to finally land.',
    intro: [
      'We tell strangers things we have never told our parents. We post tributes online for people we never properly thanked in person. Family is the conversation we keep meaning to have, until one day we cannot.',
      'A Living Family Announcement is for the things you have been meaning to say for years — to a parent, a sibling, a child, a grandparent. Not at a funeral. Not in a card. Now. While they can read it. While they can write back.',
      'These examples show how to write family announcements that are honest, specific, and gentle — even when the relationship has been complicated.',
    ],
    whenToSend: [
      'A parent is getting older and you have not said the real thing.',
      'A sibling you drifted from is finally back in your life.',
      'A child is growing up and you want them to keep your voice.',
      'A grandparent who shaped you needs to know it before you cannot tell them.',
    ],
    howToWrite: [
      'Start with one specific memory only the two of you share.',
      'Avoid sweeping statements. Avoid "always" and "never."',
      'If the relationship is complicated, name that gently — do not pretend.',
      'End with a sentence that opens a door, not closes one.',
    ],
    examples: [
      {
        headline: 'To my father, late',
        attribution: 'From Khalid · for his father Hassan',
        pullQuote:
          'You taught me how to be quiet without being absent. I am still learning it. I will keep learning it.',
        body:
          'Baba — you are not a man of many words and I have inherited that. So here is a short one, in writing, where it cannot get lost. You taught me how to be quiet without being absent. I am still learning it. I will keep learning it. Thank you for the years of small, steady things. The school runs. The repaired bicycles. The way you sat in the room when I had nothing to say. I love you. Plainly. From your son.',
      },
      {
        headline: 'To my sister, after the drift',
        attribution: 'From Bea · for her sister Anna',
        pullQuote:
          'I do not want us to be polite again. I want us to be sisters. I want to start now.',
        body:
          'Anna — we have been polite for too long. Years of it. I do not want us to be polite again. I want us to be sisters. I want to start now. I am sorry for my part in why we drifted. I am not asking you to be sorry for yours. I am just saying: I miss you. I would like to know your life again. I am here when you are ready. Your sister, always.',
      },
      {
        headline: 'A letter for my daughter to keep',
        attribution: 'From Rebecca · for her daughter Mira',
        pullQuote:
          'If you ever forget who you are, come back to this sentence: you are loved on purpose, by name.',
        body:
          'Mira — you are nine. By the time you read this with grown-up eyes you will have forgotten a lot. I am writing this so you have something to come back to. If you ever forget who you are, come back to this sentence: you are loved on purpose, by name. Not because of what you do. Because of who you are. I am proud of you in advance, for everything you will become. Your mum.',
      },
    ],
    closingThought:
      'Family is not waiting for the right moment. The right moment is the one you make.',
    accent: 'accent',
    atmosphere: 'hearth-glow',
  },
};

export const CATEGORY_ORDER: CategorySlug[] = [
  'love',
  'gratitude',
  'family',
  'birthday',
  'anniversary',
  'apology',
];
